import type {
  CorpusImportEvent,
  CorpusImportPreview,
  InstalledCorpusPack,
  OperationResult,
  PackInstallPreview,
  PackUninstallPreview,
} from '@/domain/Models';
import type { CorpusRepository } from '@/domain/Repositories';
import { loadBundledBytes, requestPersistentStorage } from './BundledLoader';
import { sha256Hex } from './Crypto';
import { db, getMeta } from './Database';
import { packInstaller } from './PackInstaller';
import type { ValidatedCorpus } from './PackageModels';
import { validateCorpusPackage } from './PackageValidator';

interface PreviewEntry {
  pack: ValidatedCorpus;
  bundled: boolean;
}

function failure(error: unknown): OperationResult {
  const raw = String(error);
  const code = raw.replace(/^Error:\s*/, '').split(':')[0] ?? 'CORPUS_IMPORT_FAILED';
  return {
    ok: false,
    errorCode: code,
    message: `语料操作失败（${code}）`,
  };
}

export class CorpusRepositoryImpl implements CorpusRepository {
  private previews = new Map<string, PreviewEntry>();
  private planByPreview = new Map<string, string>();

  async activeVersion(): Promise<string | null> {
    return getMeta('active_corpus_content_version');
  }

  async listInstalledPacks(): Promise<InstalledCorpusPack[]> {
    return packInstaller.listInstalled();
  }

  /**
   * Phase 1 — validate and preview. The package bytes stay in memory only;
   * nothing is written to the database until activatePreview() succeeds.
   */
  async inspectPackage(file: File): Promise<CorpusImportPreview | OperationResult> {
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const pack = await validateCorpusPackage(bytes);
      const previewId = `preview-${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
      this.previews.set(previewId, { pack, bundled: false });
      const plan = await packInstaller.inspect([pack], { bundled: false, sizeBytes: bytes.byteLength });
      this.planByPreview.set(previewId, plan.planId);

      const activeVer = await getMeta('active_corpus_content_version');
      const packageKey = `${pack.manifest.packageId}:${pack.manifest.contentVersion}`;
      const c = pack.manifest.counts;
      const [vocabSessions, scenarioSessions] = await Promise.all([
        db.vocabCheckpoints.where('status').equals('IN_PROGRESS').count(),
        db.scenarioSessions.where('status').equals('IN_PROGRESS').count(),
      ]);

      return {
        previewId,
        packageKey,
        contentVersion: pack.manifest.contentVersion,
        schemaVersion: pack.manifest.schemaVersion,
        counts: {
          vocabulary: c.vocabulary,
          phrases: c.phrases,
          examples: c.examples,
          scenarios: c.scenarios,
          dialogueTurns: c.dialogueTurns,
          dialoguePairs: c.dialoguePairs,
          audioAssets: c.audioAssets,
        },
        totalSizeBytes: bytes.byteLength,
        audioCount: pack.audioAssets.length,
        activeContentVersion: activeVer,
        replacesActive: false,
        activeSessionImpact: {
          vocabularySessions: vocabSessions,
          scenarioSessions,
        },
      };
    } catch (e) {
      return failure(e);
    }
  }

  /**
   * Phase 2 — activate a previewed package inside a single transaction.
   * Any failure rolls the whole transaction back; old corpus, learning
   * memory and audio are untouched. A FAILED event is recorded on error.
   */
  async activatePreview(previewId: string): Promise<OperationResult> {
    const entry = this.previews.get(previewId);
    if (!entry) {
      return { ok: false, errorCode: 'PREVIEW_NOT_FOUND', message: '预览已失效，请重新选择文件' };
    }
    try {
      const planId = this.planByPreview.get(previewId);
      const result = planId
        ? await packInstaller.commit(planId)
        : await this.install(entry.pack, entry.bundled);
      if (result.ok && planId) {
        await this.logEvent(
          'CORPUS',
          entry.bundled ? 'RESTORED' : 'ACTIVATED',
          `${entry.pack.manifest.packageId}:${entry.pack.manifest.contentVersion}`,
          entry.pack.manifest.contentVersion,
          null,
        );
      }
      this.previews.delete(previewId);
      this.planByPreview.delete(previewId);
      return result;
    } catch (e) {
      this.previews.delete(previewId);
      await this.logEvent('CORPUS', 'FAILED', null, null, String(e).replace(/^Error:\s*/, ''));
      return failure(e);
    }
  }

  /**
   * Phase 3 — discard a preview without touching the database.
   */
  async discardPreview(previewId: string): Promise<void> {
    const planId = this.planByPreview.get(previewId);
    if (planId) packInstaller.discard(planId);
    this.previews.delete(previewId);
    this.planByPreview.delete(previewId);
  }

  async inspectPackages(files: File[]): Promise<PackInstallPreview | OperationResult> {
    try {
      const packs = [];
      let size = 0;
      for (const file of files) {
        const bytes = new Uint8Array(await file.arrayBuffer());
        size += bytes.byteLength;
        packs.push(await validateCorpusPackage(bytes));
      }
      return await packInstaller.inspect(packs, { sizeBytes: size });
    } catch (e) {
      return failure(e);
    }
  }

  async commitInstall(planId: string): Promise<OperationResult> {
    return packInstaller.commit(planId);
  }

  async discardInstall(planId: string): Promise<void> {
    packInstaller.discard(planId);
  }

  async setPackEnabled(packageId: string, enabled: boolean): Promise<OperationResult> {
    return packInstaller.setEnabled(packageId, enabled);
  }

  async inspectUninstall(packageId: string): Promise<PackUninstallPreview | OperationResult> {
    try {
      return await packInstaller.inspectUninstall(packageId);
    } catch (e) {
      return failure(e);
    }
  }

  async confirmUninstall(previewId: string): Promise<OperationResult> {
    try {
      return await packInstaller.confirmUninstall(previewId);
    } catch (e) {
      return failure(e);
    }
  }

  /**
   * Activate a corpus package downloaded from the content catalog.
   * Verifies the SHA-256 before validating and installing; any failure
   * rolls back atomically and leaves old corpus/learning data untouched.
   */
  async activateDownloaded(
    bytes: Uint8Array,
    sha256: string,
    onProgress?: (p: number, msg: string) => void,
  ): Promise<OperationResult> {
    try {
      onProgress?.(8, '校验下载完整性…');
      const actual = await sha256Hex(bytes);
      if (actual.toLowerCase() !== sha256.toLowerCase()) {
        return { ok: false, errorCode: 'CORPUS_DOWNLOAD_CHECKSUM_MISMATCH', message: '下载校验失败' };
      }
      const pack = await validateCorpusPackage(bytes);
      return await this.install(pack, false, onProgress, bytes.byteLength);
    } catch (e) {
      await this.logEvent('CORPUS', 'FAILED', null, null, String(e).replace(/^Error:\s*/, ''));
      return failure(e);
    }
  }

  async restoreBundled(
    onProgress?: (p: number, msg: string) => void,
  ): Promise<OperationResult> {
    try {
      onProgress?.(5, '读取内置语料…');
      const bytes = await loadBundledBytes('bundled.besspack');
      const pack = await validateCorpusPackage(bytes);
      return await this.install(pack, true, onProgress, bytes.byteLength);
    } catch (e) {
      return failure(e);
    }
  }

  async ensureBundledActivated(
    onProgress?: (p: number, msg: string) => void,
  ): Promise<void> {
    if ((await this.activeVersion()) !== null) return;
    const result = await this.restoreBundled(onProgress);
    if (!result.ok) throw new Error(result.errorCode ?? 'CORPUS_INSTALL_FAILED');
  }

  async listImportEvents(): Promise<CorpusImportEvent[]> {
    const rows = await db.corpusImportEvents.reverse().sortBy('createdAtEpochMs');
    return rows
      .map((r) => ({
        id: r.id,
        kind: r.kind,
        action: r.action as CorpusImportEvent['action'],
        packageKey: r.packageKey,
        contentVersion: r.contentVersion,
        createdAtEpochMs: r.createdAtEpochMs,
        detail: r.detail,
      }))
      .reverse();
  }

  private async install(
    pack: ValidatedCorpus,
    bundled: boolean,
    onProgress?: (p: number, msg: string) => void,
    sizeBytes?: number,
  ): Promise<OperationResult> {
    onProgress?.(10, '校验语料包…');
    await requestPersistentStorage();
    onProgress?.(30, '写入学习库…');
    const preview = await packInstaller.inspect([pack], { bundled, sizeBytes });
    if (preview.errors.length > 0) {
      return { ok: false, errorCode: preview.errors[0]!, message: '语料包存在冲突，已取消安装' };
    }
    const result = await packInstaller.commit(preview.planId);
    if (!result.ok) return result;
    await this.logEvent(
      'CORPUS',
      bundled ? 'RESTORED' : 'ACTIVATED',
      `${pack.manifest.packageId}:${pack.manifest.contentVersion}`,
      pack.manifest.contentVersion,
      null,
    );
    onProgress?.(100, bundled ? '内置语料已激活' : '语料包已导入');
    return {
      ok: true,
      errorCode: null,
      message: bundled ? '内置语料已恢复' : '语料包已导入',
    };
  }

  private async logEvent(
    kind: 'CORPUS' | 'ARTICLE',
    action: 'ACTIVATED' | 'FAILED' | 'RESTORED',
    packageKey: string | null,
    contentVersion: string | null,
    detail: string | null,
  ): Promise<void> {
    await db.corpusImportEvents.put({
      id: `evt-${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`,
      kind,
      action,
      packageKey,
      contentVersion,
      createdAtEpochMs: Date.now(),
      detail,
    });
  }
}
